import { motion, useReducedMotion } from "framer-motion";
import { Check } from "lucide-react";

const CARDS = [
  {
    n: "01",
    title: "Glass & Glazing",
    testid: "service-card-glass",
    items: ["Glass replacement", "Mirrors", "Shower screen replacement", "Window replacement"],
  },
  {
    n: "02",
    title: "Property Maintenance",
    testid: "service-card-property",
    items: ["General house repairs", "Painting & touch-ups", "Flat pack assembly", "General handyman work"],
  },
  {
    n: "03",
    title: "Garden Maintenance",
    testid: "service-card-garden",
    items: ["Lawn mowing", "Whipper snipping", "Weed control", "Garden tidy-ups"],
  },
];

const EASE = [0.16, 1, 0.3, 1];

export const Services = () => {
  const reduce = useReducedMotion();

  return (
    <section id="services" data-testid="services-section" className="bg-sand py-20 md:py-28">
      <div className="safe-x mx-auto max-w-7xl">
        <motion.div
          initial={reduce ? false : { opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: EASE }}
        >
          <p className="eyebrow">Services</p>
          <h2 className="mt-4 max-w-3xl font-display text-4xl font-bold uppercase leading-[0.95] text-navy sm:text-5xl lg:text-6xl">
            One number. <span className="text-outline-orange">Every job.</span>
          </h2>
        </motion.div>

        <div className="mt-14 grid gap-6 md:grid-cols-2 xl:grid-cols-4">
          {CARDS.map((card, i) => (
            <motion.article
              key={card.n}
              data-testid={card.testid}
              initial={reduce ? false : { opacity: 0, y: 36 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, ease: EASE, delay: i * 0.1 }}
              className="group flex flex-col border-t-8 border-orange bg-white transition-transform duration-300 hover:-translate-y-1.5"
            >
              <div className="flex items-baseline justify-between px-7 pt-7">
                <span className="font-display text-5xl font-bold text-navy/10 transition-colors duration-300 group-hover:text-orange">
                  {card.n}
                </span>
              </div>
              <h3 className="px-7 pt-2 font-display text-2xl font-semibold uppercase tracking-wide text-navy">
                {card.title}
              </h3>
              <ul className="mt-5 space-y-3 px-7 pb-8">
                {card.items.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm text-navy/75 md:text-base">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-orange" aria-hidden="true" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.article>
          ))}

          <motion.article
            data-testid="service-card-one-number"
            initial={reduce ? false : { opacity: 0, y: 36 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, ease: EASE, delay: 0.3 }}
            className="grain relative flex flex-col justify-between border-t-8 border-orange bg-navy px-7 py-8 text-white transition-transform duration-300 hover:-translate-y-1.5"
          >
            <div>
              <h3 className="font-display text-2xl font-semibold uppercase tracking-wide text-orange">
                Why call one number?
              </h3>
              <p className="mt-5 text-sm leading-relaxed text-white/75 md:text-base">
                Cracked pane, sticking door and a jungle out back? For most people that&rsquo;s three
                different tradies. For us, it&rsquo;s Tuesday.
              </p>
              <p className="mt-4 text-sm leading-relaxed text-white/75 md:text-base">
                One call, one visit, one bill &mdash; the whole list gets done.
              </p>
            </div>
            <a
              href="tel:0424424600"
              data-testid="service-card-call-button"
              className="btn-primary mt-8 justify-center"
            >
              0424 424 600
            </a>
          </motion.article>
        </div>
      </div>
    </section>
  );
};
